/**
 * Game system registration
 *
 * Importing an adapter package registers it with the core system registry as a
 * side effect. This module is the one place that happens on the web side, so
 * there is a single answer to "which systems does the client know about".
 *
 * A campaign selects its system with `settings.systemId`. Anything unset, or
 * set to a system with no adapter here, runs as 5e — which is every campaign
 * that predates this wiring.
 */

import '@machina/system-dnd5e';
import '@machina/system-doomsworn';

import { getSystem, getRegisteredSystems, setActiveSystem } from '@machina/core';

export const DEFAULT_SYSTEM_ID = 'dnd-5e';

/** Systems a campaign can be set to, for pickers. */
export function availableSystems(): Array<{ id: string; name: string }> {
  return getRegisteredSystems().flatMap(id => {
    const adapter = getSystem(id);
    return adapter ? [{ id, name: adapter.name }] : [];
  });
}

/**
 * Activate the system a campaign runs on. Called on campaign entry, before any
 * rolls are made.
 *
 * Returns the id actually activated, which is not always the one asked for: an
 * unregistered system falls back to 5e rather than leaving the previous
 * campaign's system active. Carrying a stale system across campaigns would be
 * the worse failure — you would be playing the last campaign's rules without
 * anything on screen saying so.
 */
export function activateCampaignSystem(systemId?: string): string {
  const wanted = systemId || DEFAULT_SYSTEM_ID;

  if (setActiveSystem(wanted)) {
    if (wanted !== DEFAULT_SYSTEM_ID) {
      console.log(`[systems] Active game system: ${getSystem(wanted)?.name ?? wanted}`);
    }
    return wanted;
  }

  console.warn(`[systems] No adapter registered for "${wanted}" — falling back to ${DEFAULT_SYSTEM_ID}.`);
  setActiveSystem(DEFAULT_SYSTEM_ID);
  return DEFAULT_SYSTEM_ID;
}
